import {
  deleteRequest, deleteRequestWithBody, getFileRequest, getRequest, postRequest, putRequest,
} from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';
import type { FloorPlanRooms } from '../models/floorplan-room';
import type { FloorPlanLayersPayload } from '../utils/serialize-floorplan';
import type { StoredFloorPlan } from '../utils/normalize-floorplan';

/**
 * The editor's own view of the floorplan API.
 *
 * Deliberately not shared with `check-in-employees/services/floorplan.service.ts`: that one
 * types the response as the booking viewer's model, which has no label layer and no
 * editable properties. Both go through `core/utils/api.ts`, so they share the Keycloak
 * token handling that actually matters.
 */

export interface SaveResult {
  readonly saved: boolean;
  /** Per-layer messages from the server's own validation, when it rejected the save. */
  readonly details?: Record<string, string>;
  readonly error?: string;
  /** The plan was changed by someone else after it was opened, so nothing was written. */
  readonly conflict?: boolean;
}

interface SaveErrorResponse {
  readonly error?: string;
  readonly details?: Record<string, string>;
}

/**
 * The office's floorplans, with their desk shapes marked editable.
 *
 * `tagDeskShapes` asks the server to mark the floor polygons that draw desks with
 * `elementType:"desk"`, which is the only thing that separates an editable desk shape from
 * locked backdrop. Most stored plans were imported before the editor existed and carry no
 * such marking, so without it their desk drawings arrive as walls: visible, but impossible to
 * select, move, resize or rotate.
 *
 * Deliberately a flag rather than server-wide behaviour. The check-in overview and the mobile
 * app read this same endpoint, and the app uses that property to colour desk furniture — so
 * they keep getting exactly what is stored, and only the editor asks for the marking.
 */
export const getFloorPlansForOffice = async (
  officeId: string,
  keycloak: KeycloakContext,
): Promise<StoredFloorPlan[] | undefined> => {
  const apiResponse = await getRequest<StoredFloorPlan[]>(
    // Unpublished plans too: the editor is where they are finished and published.
    `floorplan?officeId=${officeId}&tagDeskShapes=true&includeUnpublished=true`,
    keycloak,
  );

  if (apiResponse.error) {
    return undefined;
  }

  return apiResponse.json;
};

/**
 * The rooms this plan's desks can belong to, and the room each belongs to now.
 *
 * A separate call from the floorplan itself because the answer lives in a different place:
 * booking reads the Workplaces table, and a `roomId` in the stored GeoJSON is only ever an
 * echo of it — one that no floorplan imported before the editor existed carries at all.
 */
/**
 * Why the rooms could not be read, when they could not be.
 *
 * Kept apart from "there are none" on purpose. Both used to arrive as an empty list, so a
 * refused request looked exactly like an office with no rooms — and, because a desk is drawn
 * bookable only when it appears in the assignments, it also painted every desk on the plan
 * as unbookable. Three different situations reported as one, and the two wrong ones were the
 * alarming-looking ones.
 */
export type FloorPlanRoomsFailure = 'forbidden' | 'failed';

export type FloorPlanRoomsResult =
  | {
    readonly status: 'ok';
    readonly rooms: FloorPlanRooms['rooms'];
    readonly assignments: FloorPlanRooms['assignments'];
    /** The office's fill-in period, for stating the retirement calendar rule in words. */
    readonly planPeriodDays: number;
    /** Earliest schedulable retirement date, resolved server-side in the office's timezone. */
    readonly earliestRetirementDate: string;
    readonly timeZone: string;
  }
  | { readonly status: FloorPlanRoomsFailure };

export const getFloorPlanRooms = async (
  floorPlanId: string,
  keycloak: KeycloakContext,
): Promise<FloorPlanRoomsResult> => {
  const apiResponse = await getRequest<FloorPlanRooms>(`floorplan/${floorPlanId}/rooms`, keycloak);

  if (apiResponse.error || !apiResponse.json) {
    // 401 and 403 are the same thing to the admin standing in front of it: this endpoint
    // needs administrator rights on this particular office, which the realm role that opened
    // the editor does not by itself grant.
    const forbidden = apiResponse.status === 401 || apiResponse.status === 403;

    return { status: forbidden ? 'forbidden' : 'failed' };
  }

  return {
    status: 'ok',
    rooms: apiResponse.json.rooms ?? [],
    assignments: apiResponse.json.assignments ?? [],
    // Defaulted rather than assumed present, so an older API that predates these fields leaves
    // the editor without a retirement calendar instead of with an invalid one.
    planPeriodDays: apiResponse.json.planPeriodDays ?? 0,
    earliestRetirementDate: apiResponse.json.earliestRetirementDate ?? '',
    timeZone: apiResponse.json.timeZone ?? '',
  };
};

export const saveFloorPlanLayers = async (
  floorPlanId: string,
  payload: FloorPlanLayersPayload,
  keycloak: KeycloakContext,
): Promise<SaveResult> => {
  const apiResponse = await putRequest<SaveErrorResponse>(`floorplan/${floorPlanId}/layers`, keycloak, payload);

  if (apiResponse.error) {
    // The endpoint reports which layer was rejected and why, so the editor can say that
    // rather than "something went wrong". A 409 is different in kind: nothing is wrong with
    // the submission, it was simply built on a copy that is no longer current.
    return {
      saved: false,
      error: apiResponse.error,
      details: apiResponse.json?.details,
      conflict: apiResponse.status === 409,
    };
  }

  return { saved: true };
};

/**
 * The filename the server asked for, or undefined when it did not say.
 *
 * `getFileRequest` hands back the raw fetch `Response` under the `ApiResponse` type, which
 * does not describe its headers — hence the narrowing here rather than a property access.
 */
const fileNameFrom = (response: unknown): string | undefined => {
  const { headers } = response as { headers?: Headers };
  const match = headers?.get('content-disposition')?.match(/filename="?([^";]+)"?/i);

  return match?.[1];
};

/**
 * Downloads the five layers as standalone GeoJSON files.
 *
 * Exports what is stored, not what is on screen — the endpoint reads the database. The
 * caller warns about unsaved changes before getting here.
 */
export const exportFloorPlan = async (
  floorPlanId: string,
  keycloak: KeycloakContext,
): Promise<boolean> => {
  const apiResponse = await getFileRequest(`floorplan/${floorPlanId}/export`, 'application/zip', keycloak);

  // getFileRequest hands back the raw fetch Response, so a failure shows up as a status
  // rather than as the `error` field the JSON helpers set.
  if (apiResponse.error || apiResponse.status >= 400 || !apiResponse.blob) {
    return false;
  }

  const blob = await apiResponse.blob();
  const url = window.URL.createObjectURL(blob);
  const link = window.document.createElement('a');

  link.href = url;
  // The endpoint names the file after the office and storey, which is far more use on disk
  // than a bare id. Falling back to the id only matters when the header is unreadable.
  link.download = fileNameFrom(apiResponse) ?? `floorplan-${floorPlanId}.zip`;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return true;
};

/**
 * Why the reservations could not be read, when they could not be.
 *
 * Same three-way split as the rooms call, for the same reason: a refused request and a desk
 * nobody has booked both used to look like an empty list, and only one of them is something
 * an administrator can do anything about.
 */
export type WorkplaceReservationsFailure = 'forbidden' | 'notFound' | 'failed';

export type WorkplaceReservationSource = 'Reservation' | 'GroupReservation' | 'CheckIn';

export interface WorkplaceReservation {
  /** ISO date at UTC midnight, the way the agenda stores a calendar day. */
  readonly date: string;
  readonly firstName?: string | null;
  readonly lastName?: string | null;
  /** Null for a walk-in check-in with no agenda entry behind it, which has nobody to name. */
  readonly email?: string | null;
  readonly morning: boolean;
  readonly afternoon: boolean;
  readonly source: WorkplaceReservationSource;
}

export interface WorkplaceReservations {
  readonly workplaceId: string;
  readonly roomName: string;
  readonly planPeriodDays: number;
  readonly windowStart: string;
  /** Inclusive: the last day of the fill-in period. */
  readonly windowEnd: string;
  readonly timeZone: string;
  readonly reservations: WorkplaceReservation[];
  /**
   * Every booking on or after `windowStart` with no upper bound, or absent when the call did
   * not ask for it.
   *
   * Asked for by sending `from` with no `to`. That is the retirement question — "would
   * withdrawing this desk on this date strand anybody, ever" — and `reservations` cannot answer
   * it, because the server caps that list at 366 days and a desk in this data already carries
   * bookings 1.6 years out. A warning that under-reports is worse than no warning, so this
   * total is deliberately exempt from the cap.
   */
  readonly totalFromWindowStart?: number | null;
}

/**
 * A custom span of days to report, instead of the office's fill-in period.
 *
 * Both ends are `yyyy-MM-dd`; both are optional, and sending neither is what asks for the
 * fill-in period. The server floors `from` at today and caps the span, so these are a request
 * rather than a promise — read the window back off the response.
 */
export interface WorkplaceReservationsRange {
  readonly from?: string;
  readonly to?: string;
}

export type WorkplaceReservationsResult =
  | { readonly status: 'ok'; readonly data: WorkplaceReservations }
  | { readonly status: WorkplaceReservationsFailure };

/**
 * Everyone who has booked one desk, for every day of the office's fill-in period.
 *
 * Read on demand rather than alongside the plan: it names people, it is only ever wanted for
 * the one desk an admin is looking at, and a plan can carry hundreds of desks.
 */
export const getWorkplaceReservations = async (
  workplaceId: string,
  keycloak: KeycloakContext,
  range?: WorkplaceReservationsRange,
): Promise<WorkplaceReservationsResult> => {
  const query = new URLSearchParams();

  if (range?.from) {
    query.set('from', range.from);
  }

  if (range?.to) {
    query.set('to', range.to);
  }

  const apiResponse = await getRequest<WorkplaceReservations>(
    `floorplan/workplaces/${workplaceId}/reservations${query.toString() ? `?${query}` : ''}`,
    keycloak,
  );

  if (apiResponse.error || !apiResponse.json) {
    if (apiResponse.status === 404) {
      // The desk is drawn but has no Workplaces row — nothing was ever bookable here, which
      // is a different statement from "nobody booked it".
      return { status: 'notFound' };
    }

    const forbidden = apiResponse.status === 401 || apiResponse.status === 403;

    return { status: forbidden ? 'forbidden' : 'failed' };
  }

  return { status: 'ok', data: { ...apiResponse.json, reservations: apiResponse.json.reservations ?? [] } };
};

export type WorkplaceQrCodeFailure = 'forbidden' | 'notFound' | 'failed';

export interface WorkplaceQrCode {
  readonly workplaceId: string;
  readonly roomName: string;
  /** The eight characters printed under the sticker, or null when no sticker is linked. */
  readonly shortCode?: string | null;
}

export type WorkplaceQrCodeResult =
  | { readonly status: 'ok'; readonly data: WorkplaceQrCode }
  | { readonly status: WorkplaceQrCodeFailure };

/**
 * The short code of the QR sticker on this desk, if it has one.
 *
 * A code rather than an image: the editor draws the QR itself, so the office does not pay for
 * a picture on every desk click. A desk with no sticker comes back as `ok` with no code —
 * that is the normal state for most desks, not a failure.
 */
export const getWorkplaceQrCode = async (
  workplaceId: string,
  keycloak: KeycloakContext,
): Promise<WorkplaceQrCodeResult> => {
  const apiResponse = await getRequest<WorkplaceQrCode>(
    `floorplan/workplaces/${workplaceId}/qr-code`,
    keycloak,
  );

  if (apiResponse.error || !apiResponse.json) {
    if (apiResponse.status === 404) {
      return { status: 'notFound' };
    }

    const forbidden = apiResponse.status === 401 || apiResponse.status === 403;

    return { status: forbidden ? 'forbidden' : 'failed' };
  }

  return { status: 'ok', data: apiResponse.json };
};

/**
 * Scheduling and clearing a desk's withdrawal from service.
 *
 * A desk removed from the drawing is not removed at all: the dot on the map and the bookable
 * `Workplaces` row are separate things, and nothing in the booking path reads the drawing. So
 * deleting the shape leaves the desk bookable and its QR sticker still scanning. Retirement is
 * how a desk actually goes: unbookable from a chosen date, with everything before that date
 * left to go ahead as normal, and nothing deleted.
 */

export type WorkplaceRetirementFailure =
  | 'forbidden'
  | 'notFound'
  /** The date falls inside the office's fill-in period, so the desk can still be booked past it. */
  | 'insideBookingWindow'
  | 'dateInThePast'
  /** The background job has already applied it; capacity, bookings and the QR link have all moved. */
  | 'alreadyApplied'
  | 'failed';

export interface WorkplaceRetirement {
  readonly workplaceId: string;
  /** `yyyy-MM-dd`, or null once a retirement has been cancelled. */
  readonly retiresOn?: string | null;
  /** The earliest date the server would accept: today plus the office's fill-in period. */
  readonly earliestRetirementDate: string;
  readonly planPeriodDays: number;
  readonly timeZone: string;
}

export type WorkplaceRetirementResult =
  | { readonly status: 'ok'; readonly data: WorkplaceRetirement }
  | { readonly status: WorkplaceRetirementFailure };

/**
 * Maps the endpoint's status codes onto the reasons the dialog can actually say something
 * about. 485/486/488 are this API's own codes, mirroring the agenda endpoints so a client
 * already handling "in the past" and "outside the window" needs no new vocabulary.
 */
const retirementFailureFor = (status: number): WorkplaceRetirementFailure => {
  if (status === 404) {
    return 'notFound';
  }

  if (status === 401 || status === 403) {
    return 'forbidden';
  }

  if (status === 485) {
    return 'dateInThePast';
  }

  if (status === 486) {
    return 'insideBookingWindow';
  }

  if (status === 488) {
    return 'alreadyApplied';
  }

  return 'failed';
};

/**
 * Schedule the desk to stop being bookable from `retiresOn` onward.
 *
 * The date itself is the first unbookable day. Saved through its own endpoint rather than
 * folded into the layer save, because retirement is a property of the desk record and not of
 * the drawing — coupling them would make retiring a desk fail whenever the plan happened to
 * have an unrelated validation problem.
 */
export const scheduleWorkplaceRetirement = async (
  workplaceId: string,
  retiresOn: string,
  keycloak: KeycloakContext,
  allowInsideBookingWindow = false,
): Promise<WorkplaceRetirementResult> => {
  const apiResponse = await putRequest<WorkplaceRetirement>(
    `floorplan/workplaces/${workplaceId}/retirement`,
    keycloak,
    // Sent explicitly rather than omitted when false. The server refuses a date inside the
    // fill-in period without it (486), and that default is the point: the disruptive case has to
    // be chosen.
    { retiresOn, allowInsideBookingWindow },
  );

  if (apiResponse.error || !apiResponse.json) {
    return { status: retirementFailureFor(apiResponse.status) };
  }

  return { status: 'ok', data: apiResponse.json };
};

/**
 * Clear a scheduled retirement, putting the desk back to ordinary service.
 *
 * Available any time before the date arrives, and there is nothing to restore beyond the date
 * itself — retirement never edited the floorplan geometry, so the desk never lost its dot.
 */
export const cancelWorkplaceRetirement = async (
  workplaceId: string,
  keycloak: KeycloakContext,
): Promise<WorkplaceRetirementResult> => {
  const apiResponse = await deleteRequest<WorkplaceRetirement>(
    `floorplan/workplaces/${workplaceId}/retirement`,
    keycloak,
  );

  if (apiResponse.error || !apiResponse.json) {
    return { status: retirementFailureFor(apiResponse.status) };
  }

  return { status: 'ok', data: apiResponse.json };
};

// ---------------------------------------------------------------- removing a floorplan

/** A room whose capacity removing the plan lowers. */
export interface FloorPlanDeletionRoomImpact {
  readonly roomId: string;
  readonly roomName: string;
  readonly desks: number;
  readonly capacityBefore: number;
  readonly capacityAfter: number;
}

/** What removing a floorplan changes, counted by the server as of today at the office. */
export interface FloorPlanDeletionImpact {
  readonly floorPlanId: string;
  readonly storey: number;
  readonly desks: number;
  readonly bookableDesks: number;
  readonly futureBookings: number;
  readonly people: number;
  readonly rooms: readonly FloorPlanDeletionRoomImpact[];
  readonly hasDraft: boolean;
  /** Whether removing it has to be acknowledged: it has bookable desks. */
  readonly hasImpact: boolean;
}

export const getFloorPlanDeletionImpact = async (
  floorPlanId: string,
  keycloak: KeycloakContext,
): Promise<FloorPlanDeletionImpact | undefined> => {
  const apiResponse = await getRequest<FloorPlanDeletionImpact>(`floorplan/${floorPlanId}/deletion-impact`, keycloak);
  return apiResponse.error ? undefined : apiResponse.json;
};

export type FloorPlanRemoval = 'removed' | 'needsAcknowledgement' | 'failed';

export const deleteFloorPlan = async (
  floorPlanId: string,
  acknowledged: boolean,
  keycloak: KeycloakContext,
): Promise<FloorPlanRemoval> => {
  const apiResponse = await deleteRequestWithBody(`floorplan/${floorPlanId}`, keycloak, { acknowledged });

  if (!apiResponse.error) return 'removed';
  return apiResponse.status === 409 ? 'needsAcknowledgement' : 'failed';
};

// ---------------------------------------------------------------- floor number

/** What became of a request to give a plan another floor number. */
export type StoreyChange = 'changed' | 'invalidStorey' | 'duplicateStorey' | 'failed';

/**
 * Gives an existing floorplan another floor number. The server applies the same rules as for a
 * new plan — not below 0, not a floor another plan of the office has — and says which was broken.
 */
export const changeFloorPlanStorey = async (
  floorPlanId: string,
  storey: number,
  keycloak: KeycloakContext,
): Promise<StoreyChange> => {
  const apiResponse = await putRequest<{ floorPlanId: string; storey: number }>(`floorplan/${floorPlanId}/storey`, keycloak, { storey });

  if (!apiResponse.error) return 'changed';
  if (apiResponse.status === 409) return 'duplicateStorey';
  if (apiResponse.status === 400) return 'invalidStorey';
  return 'failed';
};

// ---------------------------------------------------------------- drafts

/** Unpublished changes to a floorplan: the submission the editor would otherwise have saved live. */
export interface FloorPlanDraft {
  readonly floorPlanId: string;
  readonly payload: FloorPlanLayersPayload;
  readonly baseLastUpdatedAt: string;
  readonly lastUpdatedAt: string;
  /** When it is scheduled to go live, if it is. */
  readonly publishAt: string | null;
  readonly authorEmail: string | null;
  /** Why the last scheduled publish did not happen. */
  readonly lastPublishError: string | null;
  /** The live plan was published after this draft was started; publishing it would be refused. */
  readonly liveChangedSince: boolean;
}

/** The plan's draft, null when it has none, undefined when it could not be read. */
export const getFloorPlanDraft = async (
  floorPlanId: string,
  keycloak: KeycloakContext,
): Promise<FloorPlanDraft | null | undefined> => {
  const apiResponse = await getRequest<FloorPlanDraft>(`floorplan/${floorPlanId}/draft`, keycloak);

  if (apiResponse.error) return undefined;
  return apiResponse.status === 204 || !apiResponse.json ? null : apiResponse.json;
};

export interface DraftSaveResult {
  readonly saved: boolean;
  readonly draft?: FloorPlanDraft;
  /** Somebody else saved the draft after it was loaded. */
  readonly conflict?: boolean;
  readonly details?: Record<string, string>;
}

export const saveFloorPlanDraft = async (
  floorPlanId: string,
  payload: FloorPlanLayersPayload,
  draftLastUpdatedAt: string | undefined,
  keycloak: KeycloakContext,
): Promise<DraftSaveResult> => {
  const apiResponse = await putRequest<FloorPlanDraft & { details?: Record<string, string> }>(
    `floorplan/${floorPlanId}/draft`,
    keycloak,
    { payload, draftLastUpdatedAt: draftLastUpdatedAt ?? null },
  );

  if (!apiResponse.error && apiResponse.json) {
    return { saved: true, draft: apiResponse.json };
  }

  return {
    saved: false,
    conflict: apiResponse.status === 409,
    details: (apiResponse.errorBody as { details?: Record<string, string> } | undefined)?.details,
  };
};

export const discardFloorPlanDraft = async (floorPlanId: string, keycloak: KeycloakContext): Promise<boolean> => {
  const apiResponse = await deleteRequest(`floorplan/${floorPlanId}/draft`, keycloak);
  return !apiResponse.error;
};

export type DraftPublishResult =
  | { readonly status: 'published' }
  | { readonly status: 'scheduled'; readonly draft: FloorPlanDraft }
  | { readonly status: 'liveConflict' | 'invalidSchedule' | 'failed' }
  | { readonly status: 'invalid'; readonly details?: Record<string, string> };

/** Publishes the draft now, or schedules it when `publishAt` is in the future. */
export const publishFloorPlanDraft = async (
  floorPlanId: string,
  publishAt: string | undefined,
  keycloak: KeycloakContext,
): Promise<DraftPublishResult> => {
  const apiResponse = await postRequest<FloorPlanDraft>(
    `floorplan/${floorPlanId}/draft/publish`,
    keycloak,
    { publishAt: publishAt ?? null },
  );

  if (!apiResponse.error) {
    return publishAt && apiResponse.json && 'publishAt' in apiResponse.json && apiResponse.json.publishAt
      ? { status: 'scheduled', draft: apiResponse.json }
      : { status: 'published' };
  }

  const code = (apiResponse.errorBody as { error?: string } | undefined)?.error;

  if (apiResponse.status === 409) return { status: 'liveConflict' };
  if (code === 'invalid-schedule') return { status: 'invalidSchedule' };
  if (apiResponse.status === 400) {
    return { status: 'invalid', details: (apiResponse.errorBody as { details?: Record<string, string> } | undefined)?.details };
  }

  return { status: 'failed' };
};

/**
 * Publishes a plan that has never been published, as it stands, so the app shows it from now
 * on. False when it could not be; a plan already published, with nothing drafted, is left as it is.
 */
export const publishFloorPlan = async (floorPlanId: string, keycloak: KeycloakContext): Promise<boolean> => {
  const apiResponse = await postRequest<unknown>(`floorplan/${floorPlanId}/publish`, keycloak, {});
  return !apiResponse.error;
};

export const cancelFloorPlanDraftSchedule = async (floorPlanId: string, keycloak: KeycloakContext): Promise<boolean> => {
  const apiResponse = await deleteRequest(`floorplan/${floorPlanId}/draft/schedule`, keycloak);
  return !apiResponse.error;
};
